import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { injectTellentAssistantResourceIdGetter } from '@core/tellent-assistant-contracts';

import { TellentAssistantMastraClientService } from '../tellent-assistant-mastra.client.service';
import { TellentAssistantThreadsActions } from './tellent-assistant-threads.state';

export const loadTellentAssistantThreads = createEffect(
  (
    actions$ = inject(Actions),
    client = inject(TellentAssistantMastraClientService),
    getResourceId = injectTellentAssistantResourceIdGetter({ optional: true }) ?? (() => null),
  ) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.fetchCollection),
      switchMap(() => {
        const resourceId = getResourceId();
        if (!resourceId) {
          return of(
            TellentAssistantThreadsActions.fetchCollectionSuccess({ payload: { data: [] } }),
          );
        }
        return client.listThreads(resourceId).pipe(
          map(data => TellentAssistantThreadsActions.fetchCollectionSuccess({ payload: { data } })),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentAssistantThreadsActions.fetchCollectionFailed({
                payload: { error },
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

export const deleteTellentAssistantThread = createEffect(
  (actions$ = inject(Actions), client = inject(TellentAssistantMastraClientService)) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.removeOnePessimistic),
      switchMap(action => {
        const threadId = action.payload.data;
        return client.deleteThread(threadId).pipe(
          map(() =>
            TellentAssistantThreadsActions.removeOnePessimisticSuccess({
              payload: { data: { id: threadId } },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentAssistantThreadsActions.removeOnePessimisticFailed({
                payload: { data: threadId, error },
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

export const updateTellentAssistantThreadPin = createEffect(
  (actions$ = inject(Actions), client = inject(TellentAssistantMastraClientService)) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.updateOnePessimistic),
      switchMap(action => {
        const { id, pinned } = action.payload.data;
        return client.setThreadPinned(id, pinned ?? false).pipe(
          map(updated =>
            TellentAssistantThreadsActions.updateOnePessimisticSuccess({
              payload: { data: updated },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentAssistantThreadsActions.updateOnePessimisticFailed({
                payload: { data: { id, pinned }, error },
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);
